import React, { useEffect, useState } from "react";
import type { CompletionSaveError } from "./lessonCompletionRecovery";

interface Props {
  error: CompletionSaveError | null;
  saved?: boolean;
  persistent: boolean;
  busy: boolean;
  otherLesson: boolean;
  onRetry: () => void;
  onSignIn: () => void;
  onReturn: () => void;
  onDiscard: () => void;
}

export default function CompletionSaveStatus({ error, saved, persistent, busy, otherLesson, onRetry, onSignIn, onReturn, onDiscard }: Props) {
  const [now, setNow] = useState(Date.now());
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  useEffect(() => {
    if (!error?.retryAt) {return;}
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [error?.retryAt]);
  const remaining = Math.max(0, Math.ceil(((error?.retryAt || 0) - now) / 1000));
  return (
    <section role="alert" aria-live="polite" className="w-full max-w-3xl mx-auto rounded-lg border border-amber-300 bg-amber-50 p-5 text-gray-900" data-testid="completion-save-status">
      <p>{otherLesson ? "A completed attempt in another unit needs your attention." : saved
        ? busy ? "Your unit has saved. Refreshing your course progress…"
          : error?.message || "Your unit has saved. Refresh course progress to confirm your completion status."
        : busy ? "Saving your completed attempt…" : error?.message || "Your completed attempt is ready to save again."}</p>
      {!persistent && <p className="mt-2">Keep this page open. Browser storage is unavailable, so refreshing or signing in on another page could lose this local attempt.</p>}
      {remaining > 0 && <p className="mt-2">Try again in {remaining} seconds.</p>}
      <div className="mt-3 flex flex-wrap gap-3">
        {otherLesson ? <button className="underline" onClick={onReturn}>Return to the unit</button>
          : error?.kind === "sign-in" && persistent ? <button className="underline" onClick={onSignIn}>Sign in to save</button>
            : <button className="underline disabled:opacity-50" disabled={busy || remaining > 0 || error?.kind === "identity"} onClick={onRetry}>
              {busy ? "Please wait…" : saved ? "Refresh course progress" : "Try saving again"}
            </button>}
        {!saved && !confirmDiscard && <button className="underline" disabled={busy} onClick={() => setConfirmDiscard(true)}>Discard this recovery attempt</button>}
      </div>
      {confirmDiscard && <div className="mt-3">
        <p>This removes the local retry data only. Any work already saved stays saved.</p>
        <button className="underline mr-4" disabled={busy} onClick={onDiscard}>Confirm discard</button>
        <button className="underline" onClick={() => setConfirmDiscard(false)}>Keep attempt</button>
      </div>}
    </section>
  );
}
